import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { exportErrorCode, requestAccountExport } from "@/api/privacy";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { AccountDeletionRequest } from "@/components/settings/AccountDeletionRequest";

export default function AccountData() {
  const { t } = useTranslation();
  const token = useAuthStore((state) => state.token);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<"started" | "partial" | null>(null);
  const controller = useRef<AbortController | null>(null);
  const objectUrl = useRef<string | null>(null);
  const revokeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function releaseDownload() {
    if (revokeTimer.current) clearTimeout(revokeTimer.current);
    revokeTimer.current = null;
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = null;
  }

  useEffect(() => {
    setPassword(""); setError(null); setNotice(null); setBusy(false);
    return () => { controller.current?.abort(); releaseDownload(); };
  }, [token]);

  async function download(event: FormEvent) {
    event.preventDefault();
    if (busy || !token || !password) return;
    const request = new AbortController();
    controller.current?.abort();
    controller.current = request;
    const sameSession = () => !request.signal.aborted && useAuthStore.getState().token === token;
    setBusy(true); setError(null); setNotice(null);
    const verification = password;
    setPassword("");
    releaseDownload();
    try {
      const result = await requestAccountExport(verification, request.signal, token);
      if (!sameSession()) return;
      objectUrl.current = URL.createObjectURL(result.archive);
      const link = document.createElement("a");
      link.href = objectUrl.current;
      link.download = "mavinty-data.zip";
      document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); }
      revokeTimer.current = setTimeout(releaseDownload, 1_000);
      setNotice(result.partial ? "partial" : "started");
    } catch (failure) {
      releaseDownload();
      const code = await exportErrorCode(failure);
      if (sameSession()) setError(code);
    } finally {
      if (sameSession()) setBusy(false);
    }
  }

  return <main className="min-h-dvh bg-cream px-4 py-8 sm:px-6">
    <div className="mx-auto max-w-2xl space-y-5">
      <nav className="flex flex-wrap gap-4 text-sm text-amber-dark" aria-label={t("account_data.navigation")}>
        <Link to="/account/notifications" className="underline">{t("notifications.title")}</Link>
        <Link to="/settings" className="underline">{t("settings.title")}</Link>
        <Link to="/pricing" className="underline">{t("account_data.subscription")}</Link>
      </nav>
      <h1 className="font-serif text-3xl text-navy-deep">{t("account_data.title")}</h1>
      <Card>
        <p className="text-[15px] text-navy">{t("account_data.description")}</p>
        <p className="mt-3 text-sm text-taupe">{t("account_data.scope")}</p>
        <p className="mt-3 text-sm text-taupe">{t("account_data.subscription_free")}</p>
        <form className="mt-5 space-y-4" onSubmit={download}>
          <Input type="password" name="export_password" autoComplete="current-password"
            label={t("settings.current_password")} required maxLength={1024}
            value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} />
          <Button type="submit" loading={busy} disabled={!password}>
            <Download className="h-4 w-4" aria-hidden="true" />
            {t(busy ? "account_data.preparing" : "account_data.download")}
          </Button>
          {error && <p role="alert" className="text-sm text-coral">{t(`account_data.errors.${error}`)}</p>}
          {notice && <p role="status" className="text-sm text-navy">{t(`account_data.${notice}`)}</p>}
        </form>
      </Card>
      <AccountDeletionRequest showExportLink={false} />
    </div>
  </main>;
}
